import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import Image from "../../../components/AppImage";
import type { IndustryApplication } from "../types";

const IndustryShowcase = () => {
  const industries: IndustryApplication[] = [
    {
      id: "1",
      name: "Retail Intelligence",
      description:
        "Transform customer behavior into actionable insights for store optimization and sales growth",
      icon: "ShoppingBag",
      benefits: [
        "40% increase in conversion rates",
        "Real-time inventory optimization",
        "Customer journey mapping",
      ],

      image: "https://images.unsplash.com/photo-1567966773954-b9b3096fb387",
      alt: "Modern retail store interior with clothing displays, customers shopping, and bright lighting showcasing merchandise",
    },
    {
      id: "2",
      name: "Traffic Management",
      description:
        "Smart city solutions for traffic flow optimization and automated violation detection",
      icon: "TrafficCone",
      benefits: [
        "30% reduction in congestion",
        "Automated ATCC compliance",
        "Incident detection in 2 seconds",
      ],

      image: "https://images.unsplash.com/photo-1677389749026-9399b9c0295d",
      alt: "Busy urban intersection with multiple lanes of traffic, vehicles moving through controlled signals during daytime",
    },
    {
      id: "3",
      name: "Enterprise Security",
      description:
        "Advanced threat detection and access control for corporate facilities and campuses",
      icon: "Shield",
      benefits: [
        "99.8% threat detection accuracy",
        "Automated incident response",
        "Perimeter breach alerts",
      ],

      image: "https://images.unsplash.com/photo-1663088522868-5c584f7ae6b0",
      alt: "Corporate office building entrance with security checkpoint, glass doors, and modern access control systems",
    },
    {
      id: "4",
      name: "Manufacturing Operations",
      description:
        "Production line monitoring, safety compliance, and quality control automation",
      icon: "Factory",
      benefits: [
        "25% productivity improvement",
        "Real-time safety monitoring",
        "Defect detection automation",
      ],

      image: "https://images.unsplash.com/photo-1679454690793-83340db232db",
      alt: "Industrial manufacturing facility with automated assembly line, robotic equipment, and workers in safety gear",
    },
    {
      id: "5",
      name: "Education",
      description:
        "AI-powered campus monitoring to enhance student safety, prevent trespassing, and streamline movement tracking.",
      icon: "GraduationCap",
      benefits: [
        "Real-time campus safety alerts",
        "Unauthorized entry detection",
        "Student movement & crowd monitoring",
      ],
      image: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f",
      alt: "University campus walkway with students and security cameras monitoring activity",
    },
    {
      id: "6",
      name: "Logistics & Warehousing",
      description:
        "Improve safety, asset protection, and workflow efficiency with real-time monitoring of warehouse operations and restricted zones.",
      icon: "Boxes",
      benefits: [
        "Forklift & vehicle movement tracking",
        "Restricted zone breach alerts",
        "Worker safety & compliance monitoring",
      ],
      image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
      alt: "Large warehouse with workers, forklifts, and surveillance cameras monitoring activities",
    },
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-background to-muted">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-accent/10 rounded-full mb-4">
            <Icon name="Briefcase" size={16} className="text-accent mr-2" />
            <span className="text-sm font-medium text-accent">
              Industry Solutions
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Tailored Intelligence for Every Sector
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Proven solutions delivering measurable results across industries
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {industries.map((industry) => (
            <div
              key={industry.id}
              className="group bg-card rounded-2xl overflow-hidden shadow-brand hover:shadow-brand-lg transition-all duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={industry.image}
                  alt={industry.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />

                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
                      <Icon
                        name={industry.icon}
                        size={24}
                        className="text-primary-foreground"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {industry.name}
                    </h3>
                  </div>
                  <p className="text-white/90 text-sm">
                    {industry.description}
                  </p>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-3 mb-6">
                  {industry.benefits.map((benefit, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="w-5 h-5 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon name="Check" size={14} className="text-success" />
                      </div>
                      <span className="text-text-secondary text-sm">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>

                <Link
                  to="/industry-applications"
                  className="inline-flex items-center gap-2 text-primary font-medium hover:gap-3 transition-all duration-300"
                >
                  Explore Solutions
                  <Icon name="ArrowRight" size={16} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default IndustryShowcase;
